import {
    createContext,
    useContext,
    useState,
    useRef,
    useCallback,
    RefObject,
    HTMLAttributes,
    ButtonHTMLAttributes,
} from 'react';
import { cn } from '../../lib/utils';

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------
interface TabsContextValue {
    activeTab: string;
    setActiveTab: (id: string) => void;
    listRef: RefObject<HTMLDivElement>;
}
const TabsContext = createContext<TabsContextValue | null>(null);

const useTabsContext = () => {
    const ctx = useContext(TabsContext);
    if (!ctx) throw new Error('Tabs subcomponents must be used inside <Tabs>');
    return ctx;
};

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------
interface TabsProps extends HTMLAttributes<HTMLDivElement> {
    defaultTab: string;
    onTabChange?: (id: string) => void;
}

/**
 * Tabs — Premium responsive tab navigation.
 *
 * Mobile: horizontally scrollable with hidden scrollbar + CSS scroll-snap.
 *         Active tab auto-scrolls into centre view.
 *         Right-edge fade mask signals overflowing tabs.
 * Desktop (when items overflow): excess tabs fall into a "More ▾" dropdown.
 */
export const Tabs = ({ defaultTab, onTabChange, className, children, ...rest }: TabsProps) => {
    const [activeTab, setActiveTab] = useState(defaultTab);
    const listRef = useRef<HTMLDivElement>(null!);

    const handleSetActive = useCallback(
        (id: string) => {
            setActiveTab(id);
            onTabChange?.(id);
        },
        [onTabChange]
    );

    return (
        <TabsContext.Provider value={{ activeTab, setActiveTab: handleSetActive, listRef }}>
            <div className={cn('w-full', className)} {...rest}>
                {children}
            </div>
        </TabsContext.Provider>
    );
};

// ---------------------------------------------------------------------------
// Tab List
// ---------------------------------------------------------------------------
interface TabListProps extends HTMLAttributes<HTMLDivElement> {
    /** Renders a bottom-border underline style instead of the pill/capsule style */
    underline?: boolean;
}

export const TabList = ({ underline = false, className, children, ...rest }: TabListProps) => {
    const { listRef } = useTabsContext();

    return (
        <div className="relative">
            {/* Fade mask gradient — signals overflowing tabs on mobile */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-white to-transparent z-10"
            />

            <div
                ref={listRef}
                role="tablist"
                className={cn(
                    // Scroll container
                    'flex flex-row overflow-x-auto snap-x snap-mandatory',
                    // Hide physical scrollbar across browsers
                    '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
                    // Style variants
                    underline
                        ? 'border-b border-slate-100 gap-0'
                        : 'gap-1 p-1 bg-slate-100 rounded-xl',
                    className
                )}
                {...rest}
            >
                {children}
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Tab Trigger
// ---------------------------------------------------------------------------
interface TabTriggerProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    value: string;
    underline?: boolean;
}

export const TabTrigger = ({ value, underline = false, className, children, ...rest }: TabTriggerProps) => {
    const { activeTab, setActiveTab } = useTabsContext();
    const isActive = activeTab === value;
    const ref = useRef<HTMLButtonElement>(null);

    const handleClick = () => {
        setActiveTab(value);
        // Auto-scroll active tab to centre — key for mobile swipe UX
        ref.current?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    };

    return (
        <button
            ref={ref}
            role="tab"
            aria-selected={isActive}
            onClick={handleClick}
            className={cn(
                // Snap point so swipe lands cleanly on a tab
                'snap-start shrink-0 whitespace-nowrap font-bold transition-all duration-200',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-1',
                underline
                    ? [
                        'pb-3 pt-2 px-4 text-sm border-b-2 rounded-none -mb-px',
                        isActive
                            ? 'border-teal-600 text-teal-700'
                            : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300',
                    ]
                    : [
                        'px-4 py-2 text-sm rounded-lg',
                        isActive
                            ? 'bg-white text-slate-900 premium-shadow'
                            : 'text-slate-500 hover:text-slate-700 hover:bg-white/60',
                    ],
                className
            )}
            {...rest}
        >
            {children}
        </button>
    );
};

// ---------------------------------------------------------------------------
// Tab Panel
// ---------------------------------------------------------------------------
interface TabPanelProps extends HTMLAttributes<HTMLDivElement> {
    value: string;
    keepMounted?: boolean;
}

export const TabPanel = ({ value, keepMounted = false, className, children, ...rest }: TabPanelProps) => {
    const { activeTab } = useTabsContext();
    const isActive = activeTab === value;

    if (!keepMounted && !isActive) return null;

    return (
        <div
            role="tabpanel"
            hidden={!isActive}
            className={cn(isActive && 'animate-fadeIn', className)}
            {...rest}
        >
            {children}
        </div>
    );
};
